import { FeatureFlag, ResolverTag, Site } from '@ps/web-core-enums';
import { addDays, format, subYears } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';

import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import Translation from '@swf/components/Translation/Translation';
import type { StatusCode } from '@swf/core/enums/statusCode';
import useFeature from '@swf/core/hooks/useFeature';
import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import { select } from '@swf/core/store';
import { translatedLink } from '@swf/core/utils';

import { ExclusionTime } from '../../shared/definitions';
import { ExclusionType } from '../definitions';
import useExclusionType from '../hooks/useExclusionType';

import {
  TXTCLI_We_apologize_for_the_inconveni_ELL,
  TXTCLI_You_will_be_able_to_request_a__ELL,
  TXTCLI_Your_account_privileges_have_b_ELL_2,
  TXTWCL_If_you_have_any_questionsX_ple_ELL_2,
  TXTWCL_This_means_you_can_no_longer_p_ELL,
  TXTWCL_This_means_you_wonXt_be_able_t_ELL_11,
  TXTWCL_This_means_you_wonXt_be_able_t_ELL_3,
  TXTWCL_This_means_you_wonXt_be_able_t_ELL_5,
  TXTWCL_WeXll_automatically_reopen_you_ELL,
  TXTWCL_When_it_ends_you_will_need_to__ELL,
  TXTWCL_XIf_you_opted_for_the_24_hour__ELL,
  TXTWCL_YouXll_be_able_to_request_to_r_ELL
} from './ExclusionResponse.messages.json';
import ExclusionResponseCAON from './ExclusionResponseCAON/ExclusionResponseCAON';
import ExclusionResponseDK from './ExclusionResponseDK/ExclusionResponseDK';
import ExclusionResponseFR from './ExclusionResponseFR/ExclusionResponseFR';
import ExclusionResponseGR from './ExclusionResponseGR/ExclusionResponseGR';
import ExclusionResponsePT from './ExclusionResponsePT/ExclusionResponsePT';
import ExclusionResponseUK from './ExclusionResponseUK/ExclusionResponseUK';
import ExclusionResponseUSNJ from './ExclusionResponseUSNJ/ExclusionResponseUSNJ';
import ExclusionResponseUSPA from './ExclusionResponseUSPA/ExclusionResponseUSPA';
import NationalExclusionResponseVegas from './NationalExclusionResponseVegas/NationalExclusionResponseVegas';

interface Props {
  error?: boolean;
  daysRequested?: number;
  timeUntil?: number | string;
  banEndDate?: string;
  nationalSelfExclusionVegas?: boolean;
  statusCode?: StatusCode;
  selfExcludedInfoFlags?: number;
}

const ExclusionResponse: React.FC<Props> = ({
  error,
  timeUntil,
  banEndDate,
  daysRequested,
  nationalSelfExclusionVegas,
  statusCode,
  selfExcludedInfoFlags
}) => {
  const showVegasContent = useSelector(select.cms.showVegasContent);
  const isIndefiniteExclusionAllowed = useSelector(select.cms.isIndefiniteExclusionAllowed);
  const site = useSelector(select.cms.getSiteId);
  const nationalExclusion = useSelector(select.account.bannedByRegulator);
  const isEUDE = useSelector(select.account.isEUDE);
  const allowAccountReopeningRequest = useSelector(select.account.allowAccountReopeningRequest);
  const indefiniteExclusion = daysRequested === ExclusionTime.Indefinite;
  const exclusionType = useExclusionType(daysRequested, nationalExclusion);
  const openWindowContactSupport = useOpenWindow({ resolverTag: ResolverTag.ContactSupport });

  const selfExclusionEndDate = timeUntil ? new Date(timeUntil) : new Date();
  const indefiniteExclusionEndDate =
    site === Site.IT
      ? addDays(new Date(), ExclusionTime.HundredEightyDays)
      : subYears(selfExclusionEndDate, 99);

  const visibilityOASISExclusionOption = useFeature(FeatureFlag.VisibilityOASISExclusionOption);
  const isCOMEUHalfYearBreak =
    [Site.COM, Site.EU].includes(site) &&
    daysRequested === ExclusionTime.HundredEightyDays &&
    !showVegasContent;
  const showAccountReopeningText =
    allowAccountReopeningRequest || exclusionType !== ExclusionType.SelfExclusion;

  switch (true) {
    case site === Site.FR:
      return (
        <ExclusionResponseFR error={error} endDate={selfExclusionEndDate} statusCode={statusCode} />
      );

    case site === Site.GR:
      return (
        <ExclusionResponseGR
          error={error}
          timeUntil={timeUntil}
          statusCode={statusCode}
          daysRequested={daysRequested}
        />
      );
    case site === Site.USPA:
      return (
        <ExclusionResponseUSPA
          error={error}
          endDate={selfExclusionEndDate}
          statusCode={statusCode}
          daysRequested={daysRequested}
          exclusionType={exclusionType}
        />
      );

    case site === Site.USNJ:
      return (
        <ExclusionResponseUSNJ
          error={error}
          timeUntil={timeUntil}
          statusCode={statusCode}
          daysRequested={daysRequested}
          exclusionType={exclusionType}
        />
      );
    case site === Site.PT:
      return (
        <ExclusionResponsePT
          error={error}
          statusCode={statusCode}
          exclusionType={exclusionType}
          timeUntil={timeUntil}
          selfExcludedInfoFlags={selfExcludedInfoFlags}
          daysRequested={daysRequested}
        />
      );
    case site === Site.DK:
      return (
        <ExclusionResponseDK
          error={error}
          statusCode={statusCode}
          exclusionType={exclusionType}
          timeUntil={timeUntil}
          daysRequested={daysRequested}
        />
      );
    case nationalSelfExclusionVegas:
      return <NationalExclusionResponseVegas error={error} banEndDate={banEndDate} />;
    case nationalExclusion && site === Site.IT:
      return <Translation {...TXTWCL_This_means_you_can_no_longer_p_ELL} />;
    case nationalExclusion && visibilityOASISExclusionOption:
      return (
        <>
          <Translation {...TXTWCL_This_means_you_wonXt_be_able_t_ELL_11} />{' '}
          <Translation
            {...TXTWCL_When_it_ends_you_will_need_to__ELL}
            values={{
              a: (msg: string) => (
                <ResolverLink resolverTag={ResolverTag.OasisReinstate}>{msg}</ResolverLink>
              )
            }}
          />{' '}
          <Translation {...TXTWCL_XIf_you_opted_for_the_24_hour__ELL} />
        </>
      );
    case nationalExclusion && showVegasContent:
      return (
        <>
          <Translation {...TXTCLI_Your_account_privileges_have_b_ELL_2} />{' '}
          <Translation {...TXTCLI_We_apologize_for_the_inconveni_ELL} />
          <Translation tagName="p" {...TXTCLI_You_will_be_able_to_request_a__ELL} />
          <Translation
            {...TXTWCL_If_you_have_any_questionsX_ple_ELL_2}
            values={{ a: translatedLink({ onClick: openWindowContactSupport }) }}
          />
        </>
      );
    case [Site.UK, Site.CAON].includes(site) &&
      (indefiniteExclusion || (daysRequested && daysRequested >= 180)):
      if (site === Site.UK) {
        return <ExclusionResponseUK daysRequested={Number(daysRequested)} />;
      }
      return <ExclusionResponseCAON indefiniteExclusion={indefiniteExclusion} />;
    case exclusionType === ExclusionType.Break:
      return (
        <>
          <Translation
            {...TXTWCL_This_means_you_wonXt_be_able_t_ELL_5}
            values={{ 0: <b>{format(selfExclusionEndDate, 'Pp')}</b> }}
          />{' '}
          <Translation
            {...(isEUDE || isCOMEUHalfYearBreak
              ? TXTWCL_YouXll_be_able_to_request_to_r_ELL
              : TXTWCL_WeXll_automatically_reopen_you_ELL)}
            values={{
              a: (msg: string) => (
                <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
              )
            }}
          />
        </>
      );
    case isIndefiniteExclusionAllowed && exclusionType === ExclusionType.SelfExclusion:
      return (
        <Translation
          {...TXTWCL_This_means_you_wonXt_be_able_t_ELL_3}
          values={{
            0: <b>{format(selfExclusionEndDate, 'Pp')}</b>
          }}
        />
      );
    default:
      return (
        <>
          <Translation
            {...(indefiniteExclusion
              ? TXTWCL_This_means_you_wonXt_be_able_t_ELL_3
              : TXTWCL_This_means_you_wonXt_be_able_t_ELL_5)}
            values={{
              0: (
                <b>
                  {format(
                    indefiniteExclusion ? indefiniteExclusionEndDate : selfExclusionEndDate,
                    'Pp'
                  )}
                </b>
              )
            }}
          />{' '}
          {!indefiniteExclusion && showAccountReopeningText && (
            <Translation
              {...TXTWCL_YouXll_be_able_to_request_to_r_ELL}
              values={{
                a: (msg: string) => (
                  <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                )
              }}
            />
          )}
        </>
      );
  }
};

export default ExclusionResponse;
